import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Power, School as SchoolIcon, Book, Calendar, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "../lib/perms";
import { useAuth } from "../lib/auth";

function useDeleteFlow(kind, endpoint) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [usage, setUsage] = useState(null);
  const start = async (row) => {
    setTarget(row); setOpen(true); setUsage(null);
    try { const u = await api.get(`${endpoint}/${row.id}/usage`); setUsage(u.data); }
    catch { setUsage({ total_dependents: 0 }); }
  };
  const confirm = async (onDone) => {
    try { await api.delete(`${endpoint}/${target.id}`); toast.success(`${kind[0].toUpperCase()+kind.slice(1)} deleted`); setOpen(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const Dlg = ({ onDone }) => (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/> Delete {kind}</DialogTitle>
          <DialogDescription className="pt-1">You are about to permanently delete <strong>{target?.name}</strong>.</DialogDescription>
        </DialogHeader>
        {!usage ? <div className="text-xs text-slate-500">Checking usage…</div>
          : usage.total_dependents === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900">Nothing is linked. Safe to delete.</div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 space-y-1">
              <div className="font-semibold">Can't delete yet — {usage.total_dependents} record{usage.total_dependents===1?"":"s"} still linked:</div>
              {"programs" in usage && usage.programs>0 && <div>· {usage.programs} program(s)</div>}
              {"b2c_applications" in usage && usage.b2c_applications>0 && <div>· {usage.b2c_applications} B2C application(s)</div>}
              {"b2b_applications" in usage && usage.b2b_applications>0 && <div>· {usage.b2b_applications} B2B application(s)</div>}
              {usage.sample_applications?.length>0 && <div className="pt-1 border-t border-amber-200">Example IDs: <span className="mono">{usage.sample_applications.map(a=>a.application_id).join(", ")}</span></div>}
              <div className="pt-1">Reassign or delete those first, then retry.</div>
            </div>
          )}
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Close</Button>
          <Button onClick={()=>confirm(onDone)} disabled={!usage || usage.total_dependents>0} className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50" data-testid={`${kind}-delete-confirm`}>Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { start, Dlg };
}

export default function MasterData() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Master Data</h1>
        <p className="text-sm text-slate-500 mt-1">Manage Colleges, Program Categories and Intakes. Deactivate to preserve history; Delete to remove permanently (blocked if in use).</p>
      </div>
      <Tabs defaultValue="colleges">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="colleges" data-testid="tab-colleges"><SchoolIcon className="w-4 h-4 mr-1"/> Colleges</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tab-programs"><Book className="w-4 h-4 mr-1"/> Program Categories</TabsTrigger>
          <TabsTrigger value="intakes" data-testid="tab-intakes"><Calendar className="w-4 h-4 mr-1"/> Intakes</TabsTrigger>
        </TabsList>
        <TabsContent value="colleges"><CollegesTab canDelete={canDelete} /></TabsContent>
        <TabsContent value="programs"><CategoriesInfo /></TabsContent>
        <TabsContent value="intakes"><IntakesTab canDelete={canDelete} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CollegesTab({ canDelete }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const load = () => api.get("/b2c/colleges?include_inactive=true").then(r=>setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    try { await api.post("/b2c/colleges", f); toast.success("Created"); setF({}); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => { await api.patch(`/b2c/colleges/${r.id}`, { is_active: !r.is_active }); load(); };
  const del = useDeleteFlow("college", "/b2c/colleges");
  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-end"><Button data-testid="add-college-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>Add College</Button></div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">City</th><th className="text-left px-4 py-3">Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3 mono text-xs">{r.code}</td>
                <td className="px-4 py-3">{r.city}, {r.country}</td>
                <td className="px-4 py-3">{r.is_active ? <span className="stage-badge bg-emerald-50 text-emerald-800 border-emerald-300">Active</span> : <span className="stage-badge bg-slate-100 text-slate-700 border-slate-300">Inactive</span>}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={()=>toggle(r)} title={r.is_active?"Deactivate":"Activate"}><Power className="w-4 h-4" /></Button>
                  {canDelete && <Button size="sm" variant="ghost" onClick={()=>del.start(r)} data-testid={`del-college-${r.name}`} title="Delete" className="text-rose-600 hover:text-rose-800"><Trash2 className="w-4 h-4" /></Button>}
                </td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No colleges yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add College</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="college-name" value={f.name||""} onChange={e=>setF({...f, name:e.target.value})}/></div>
            <div><Label>Short Code</Label><Input value={f.code||""} onChange={e=>setF({...f, code:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-3"><div><Label>City</Label><Input value={f.city||""} onChange={e=>setF({...f, city:e.target.value})}/></div><div><Label>Country</Label><Input value={f.country||"Canada"} onChange={e=>setF({...f, country:e.target.value})}/></div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button data-testid="college-save" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <del.Dlg onDone={load} />
    </div>
  );
}

function CategoriesInfo() {
  return (
    <div data-testid="program-categories-info" className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
      <div className="heading-font font-semibold text-slate-900">Program Categories</div>
      <p className="text-sm text-slate-600">Programs are now one shared list of five categories for every college, B2B and B2C. Each application keeps its own free-text Course Name. Old program records are retired through Settings → Data safety.</p>
      <div className="flex flex-wrap gap-2">{CATEGORIES.map(c => <span key={c} className="rounded-md bg-orange-50 border border-orange-200 text-orange-900 text-xs px-2 py-1">{c}</span>)}</div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function ProgramsTab({ canDelete }) {
  const [colleges, setColleges] = useState([]);
  const [rows, setRows] = useState([]);
  const [collegeFilter, setCollegeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const load = async () => {
    const [c, p] = await Promise.all([api.get("/b2c/colleges?include_inactive=true"), api.get("/b2c/programs?include_inactive=true")]);
    setColleges(c.data); setRows(p.data);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name || !f.college_id) return toast.error("Name and college required");
    try { await api.post("/b2c/programs", f); toast.success("Created"); setF({}); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => { await api.patch(`/b2c/programs/${r.id}`, { is_active: !r.is_active }); load(); };
  const del = useDeleteFlow("program", "/b2c/programs");
  const filtered = collegeFilter === "all" ? rows : rows.filter(r=>r.college_id === collegeFilter);
  const cmap = Object.fromEntries(colleges.map(c=>[c.id,c]));
  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-between items-center gap-2">
        <Select value={collegeFilter} onValueChange={setCollegeFilter}>
          <SelectTrigger className="w-72 bg-white" data-testid="program-college-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Colleges</SelectItem>{colleges.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="add-program-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>Add Program</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-3">Program</th><th className="text-left px-4 py-3">College</th><th className="text-left px-4 py-3">Level</th><th className="text-left px-4 py-3">Status</th><th></th></tr></thead>
          <tbody>
            {filtered.map(r=>(
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3">{cmap[r.college_id]?.name || "—"}</td>
                <td className="px-4 py-3">{r.level}</td>
                <td className="px-4 py-3">{r.is_active ? <span className="stage-badge bg-emerald-50 text-emerald-800 border-emerald-300">Active</span> : <span className="stage-badge bg-slate-100 text-slate-700 border-slate-300">Inactive</span>}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={()=>toggle(r)}><Power className="w-4 h-4" /></Button>
                  {canDelete && <Button size="sm" variant="ghost" onClick={()=>del.start(r)} data-testid={`del-program-${r.name}`} className="text-rose-600 hover:text-rose-800"><Trash2 className="w-4 h-4" /></Button>}
                </td>
              </tr>
            ))}
            {filtered.length===0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No programs match.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Program</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>College *</Label>
              <Select value={f.college_id||""} onValueChange={v=>setF({...f, college_id:v})}>
                <SelectTrigger data-testid="program-college"><SelectValue placeholder="Choose college"/></SelectTrigger>
                <SelectContent>{colleges.filter(c=>c.is_active).map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Program Name *</Label><Input data-testid="program-name" value={f.name||""} onChange={e=>setF({...f, name:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-3"><div><Label>Level</Label><Input placeholder="Graduate / Undergraduate" value={f.level||""} onChange={e=>setF({...f, level:e.target.value})}/></div><div><Label>Duration</Label><Input placeholder="2 years" value={f.duration||""} onChange={e=>setF({...f, duration:e.target.value})}/></div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button data-testid="program-save" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <del.Dlg onDone={load} />
    </div>
  );
}

function IntakesTab({ canDelete }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const load = () => api.get("/b2c/intakes?include_inactive=true").then(r=>setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    try { await api.post("/b2c/intakes", f); toast.success("Created"); setF({}); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => { await api.patch(`/b2c/intakes/${r.id}`, { is_active: !r.is_active }); load(); };
  const del = useDeleteFlow("intake", "/b2c/intakes");
  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-end"><Button data-testid="add-intake-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-2"/>Add Intake</Button></div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Start</th><th className="text-left px-4 py-3">Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3">{r.start_date}</td>
                <td className="px-4 py-3">{r.is_active ? <span className="stage-badge bg-emerald-50 text-emerald-800 border-emerald-300">Active</span> : <span className="stage-badge bg-slate-100 text-slate-700 border-slate-300">Inactive</span>}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={()=>toggle(r)}><Power className="w-4 h-4" /></Button>
                  {canDelete && <Button size="sm" variant="ghost" onClick={()=>del.start(r)} data-testid={`del-intake-${r.name}`} className="text-rose-600 hover:text-rose-800"><Trash2 className="w-4 h-4" /></Button>}
                </td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan={4} className="text-center py-8 text-slate-500">No intakes yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white"><DialogHeader><DialogTitle>Add Intake</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="intake-name" placeholder="September 2027" value={f.name||""} onChange={e=>setF({...f, name:e.target.value})}/></div>
            <div><Label>Start Date</Label><Input type="date" value={f.start_date||""} onChange={e=>setF({...f, start_date:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button data-testid="intake-save" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <del.Dlg onDone={load} />
    </div>
  );
}
